import styled from "styled-components";

/**
 * Brand Palette Wizard styles (deck screen 01). Tokens per concept index.md:
 * accent #9B5AF8, ink #123051, sub #565556, line #e1e1e1, bg #F6F7F9,
 * active fill tints #F1E9FE / #EDE0FD, hover #8126DB. DM Sans inherited from the
 * app shell. Circular swatches (notes6 #1). Reduced-motion guarded.
 */

const PL = "#9B5AF8";
const INK = "#123051";
const SUB = "#565556";
const LINE = "#e1e1e1";
const BG = "#F6F7F9";

export const Body = styled.div`
  padding: 4px 20px 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 768px) {
    grid-template-columns: minmax(0, 320px) 1fr;
  }
`;

/* ---- left: preview ---- */

export const PreviewCol = styled.div`
  order: 2;
  @media (min-width: 768px) {
    order: 1;
  }
`;

export const PreviewHeading = styled.div`
  font-weight: 700;
  font-size: 16px;
  color: ${INK};
`;

export const PreviewSub = styled.div`
  font-size: 13px;
  color: ${SUB};
  margin: 2px 0 12px;
`;

export const PreviewCard = styled.div`
  background: ${BG};
  border-radius: 12px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

export const PreviewName = styled.div`
  font-weight: 700;
  font-size: 15px;
  color: ${INK};
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* overlapping-circle chip row (Library card language, notes6 #1) */
export const ChipRow = styled.div`
  display: flex;
  align-items: center;
`;

export const Chip = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #98a2b3;
  margin-left: -8px;
  &:first-child {
    margin-left: 0;
  }
`;

/* ---- right: form ---- */

export const FormCol = styled.div`
  order: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (min-width: 768px) {
    order: 2;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-weight: 600;
  font-size: 13px;
  color: ${INK};
`;

export const NameInput = styled.input`
  height: 44px;
  border: 1px solid ${LINE};
  border-radius: 8px;
  padding: 0 12px;
  font-size: 15px;
  color: ${INK};
  font-family: inherit;
  background: #fff;
  &:focus {
    outline: 2px solid ${PL};
    outline-offset: 1px;
    border-color: ${PL};
  }
`;

export const ColorsHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

export const CountPill = styled.span`
  font-size: 12px;
  color: ${SUB};
`;

export const StopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const StopRow = styled.div`
  display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center;
  gap: 10px;
`;

export const Swatch = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${LINE};
  padding: 0;
  cursor: pointer;
  background-color: ${({ color }) => color};
  box-shadow: ${({ active }) => (active ? `0 0 0 3px #EDE0FD` : "none")};
  transition: box-shadow 0.15s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const HexField = styled.div`
  display: grid;
  grid-template-columns: 26px 1fr;
  align-items: center;
  height: 44px;
  border: 1px solid ${({ active }) => (active ? PL : LINE)};
  border-radius: 8px;
  padding: 0 12px;
  background: #fff;
  box-shadow: ${({ active }) => (active ? `0 0 0 3px #EDE0FD` : "none")};
`;

export const HexPrefix = styled.span`
  color: #98a2b3;
  font-size: 15px;
`;

export const HexInput = styled.input`
  border: 0;
  outline: 0;
  background: transparent;
  font-size: 15px;
  color: ${INK};
  font-family: inherit;
  letter-spacing: 0.5px;
  width: 100%;
`;

export const IconBtn = styled.button`
  width: 40px;
  height: 40px;
  border: 1px solid ${LINE};
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${SUB};
  &:hover:not(:disabled) {
    border-color: ${PL};
    color: ${PL};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const AddRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 48px;
  border: 1px dashed ${PL};
  border-radius: 10px;
  background: #fff;
  color: ${PL};
  font-weight: 600;
  font-size: 14px;
  padding: 0 16px;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: #FAF6FF;
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const AddLeft = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;
